import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import EditIcon from "../../globalStyles/icons/editIcon";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import FilterModal from "../filterModal";
import TableHeader from "../table-header";
import { salesByCategoryService } from "../../utils/services/salesByCategoryService";
import { toast } from "react-toastify";
import { formatDate } from "../../utils/formatDate";

const StyledTableCell = styled(TableCell)({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "none",
    padding: 8,
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 12, padding: 6 },
});

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--bg-paper)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
  "&:last-child td, &:last-child th": { border: 0 },
}));

const MismatchTableRow = styled(TableRow)(() => ({
  backgroundColor: "var(--bg-paper) !important",
  borderLeft: "3px solid #f59e0b !important",
  "& td": { backgroundColor: "#fff8e1 !important" },
  "&:last-child td, &:last-child th": { border: 0 },
}));

const hasMismatch = (entry) =>
  entry.calculated_revenue != null &&
  entry.total_revenue != null &&
  Number(entry.calculated_revenue).toFixed(4) !==
    Number(entry.total_revenue).toFixed(4);

const ActionPopover = ({ row, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const openPopover = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const closePopover = () => setAnchorEl(null);
  const doAction = (type) => {
    closePopover();
    if (type === "edit") onEdit(row);
    if (type === "delete") onDelete(row);
  };

  return (
    <Box>
      <IconButton
        size="small"
        onClick={openPopover}
        sx={{ color: open ? "primary.main" : "inherit" }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box display="flex" flexDirection="column" p={1}>
          <Button
            onClick={() => doAction("edit")}
            sx={{ textTransform: "none" }}
          >
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <EditIcon />
              <Typography
                fontFamily="Poppins"
                fontSize={14}
                fontWeight={500}
                color="var(--text-tertiary)"
              >
                Edit
              </Typography>
            </Box>
          </Button>
          <Button
            onClick={() => doAction("delete")}
            sx={{ textTransform: "none" }}
          >
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <DeleteIcon />
              <Typography
                fontFamily="Poppins"
                fontSize={14}
                fontWeight={500}
                color="var(--text-tertiary)"
              >
                Delete
              </Typography>
            </Box>
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

const SalesByCategoryList = ({
  data,
  onEditData,
  onDeleteData,
  filterData,
  onChangeFilter,
  clientId,
}) => {
  const theme = useTheme();
  const primary_color = theme.palette?.primary?.main || "#D13438";

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [mismatchEntry, setMismatchEntry] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [selectedFilterData, setSelectedFilterData] = useState({
    key: "",
    filter: [],
    sort: 0,
    id: "_id",
    displayKey: "name",
    fetchApi: () => {},
    loadMoreButton: false,
    options: [],
    searchKey: "",
    type: "",
  });

  const handleOpenFilter = (event, filterKey) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilterModal(true);
    setSelectedFilterData(filterData.columns.find((x) => x.key === filterKey));
  };

  const handleSaveFilter = (
    selectedOptions,
    filterKey,
    sortOrder,
    searchKey,
  ) => {
    onChangeFilter({
      key: filterKey,
      filter: selectedOptions,
      sort: sortOrder,
      searchKey,
    });
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };

  const handleCloseFilter = () => {
    setAnchorElFilter(null);
    setOpenFilterModal(false);
  };

  const handleSyncRevenue = async () => {
    if (!mismatchEntry) return;
    setSyncLoading(true);
    try {
      await salesByCategoryService.edit(
        { total_revenue: mismatchEntry.calculated_revenue },
        mismatchEntry._id,
      );
      toast.success("Revenue synced successfully.");
      setMismatchEntry(null);
      onDeleteData && onDeleteData();
    } catch (e) {
      toast.error("Failed to sync revenue.");
      console.error(e);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await salesByCategoryService.delete(deleteTarget._id);
      toast.success("Sales entry deleted successfully!");
      onDeleteData && onDeleteData();
    } catch (e) {
      toast.error("Failed to delete sales entry.");
      console.error(e);
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  // ─── TableSchema ─────────────────────────────────────────────────────────────
  const TableSchema = [
    {
      key: "sl_no",
      title: "SL NO",
      render: (item, index) => (
        <Typography fontFamily="Poppins" fontSize={12}>
          {index + 1}
        </Typography>
      ),
    },
    {
      key: "dimension",
      title: "Grouped By",
      filterKey: "dimension",
      handleOpenFilter,
      render: (item) => (
        <Typography fontFamily="Poppins" fontSize={12} fontWeight={500}>
          {item.dimension === "product_type" ? "Product Type" : "Category"}
        </Typography>
      ),
    },
    {
      key: "category",
      title: "Category / Type",
      filterKey: "category",
      handleOpenFilter,
      render: (item) => (
        <Typography fontFamily="Poppins" fontSize={12} fontWeight={500}>
          {item.dimension === "product_type"
            ? item.product_type || "—"
            : item.category?.name || "—"}
        </Typography>
      ),
    },
    ...(clientId
      ? []
      : [
          {
            key: "client",
            title: "Client",
            filterKey: "client",
            handleOpenFilter,
            render: (item) => (
              <Typography fontFamily="Poppins" fontSize={12} fontWeight={500}>
                {item.client?.business_name || "—"}
              </Typography>
            ),
          },
        ]),
    {
      key: "month",
      title: "Month",
      filterKey: "month",
      handleOpenFilter,
      render: (item) => (
        <Typography fontFamily="Poppins" fontSize={12}>
          {item.month
            ? new Date(2000, item.month - 1, 1).toLocaleString("default", {
                month: "long",
              })
            : "—"}
        </Typography>
      ),
    },
    {
      key: "year",
      title: "Year",
      filterKey: "year",
      handleOpenFilter,
      render: (item) => (
        <Typography fontFamily="Poppins" fontSize={12}>
          {item.year}
        </Typography>
      ),
    },
    {
      key: "unit_sold",
      title: "Unit Sold",
      handleOpenFilter,
      filterKey: "unit_sold",
      render: (item) => (
        <Typography fontFamily="Poppins" fontSize={12}>
          {item.unit_sold}
        </Typography>
      ),
    },
    {
      key: "total_revenue",
      title: "Revenue",
      filterKey: "total_revenue",
      handleOpenFilter,
      render: (item) => (
        <Typography fontFamily="Poppins" fontSize={12}>
          ${item.total_revenue?.toFixed(2)}
        </Typography>
      ),
    },
    {
      key: "calculated_revenue",
      title: "Calculated Revenue",
      filterKey: "calculated_revenue",
      handleOpenFilter,
      render: (item) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography fontFamily="Poppins" fontSize={12}>
            {item.calculated_revenue != null
              ? `$${item.calculated_revenue.toFixed(2)}`
              : "—"}
          </Typography>
          {hasMismatch(item) && (
            <Tooltip title="Calculated revenue differs from analytics revenue. Click to review.">
              <IconButton
                size="small"
                sx={{ color: "#f59e0b", p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setMismatchEntry(item);
                }}
              >
                <WarningAmberIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      key: "calculated_till",
      title: "Calculated Till",
      filterKey: "calculated_till",
      handleOpenFilter,
      render: (item) => (
        <Typography fontFamily="Poppins" fontSize={12}>
          {item.calculated_till
            ? formatDate(item.calculated_till)
            : "—"}
        </Typography>
      ),
    },
    {
      key: "action",
      title: "",
      width: "5%",
      render: (item) => (
        <ActionPopover
          row={item}
          onEdit={onEditData}
          onDelete={(row) => setDeleteTarget(row)}
        />
      ),
    },
  ];

  const colCount = TableSchema.length;

  return (
    <>
      {deleteLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0,0,0,0.4)",
            zIndex: 9999,
          }}
        >
          <CircularProgress color="inherit" sx={{ color: "#fff" }} />
        </Box>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid var(--border-color)",
          overflowY: "auto",
          maxHeight: "calc(100vh - 210px)",
          height: "100%",
        }}
      >
        <Table
          stickyHeader
          size="medium"
          sx={{
            border: "1px solid var(--border-color)",
            tableLayout: "auto",
          }}
        >
          <TableHead>
            <StyledTableRow>
              {TableSchema.map((column) => (
                <TableHeader
                  key={column.key}
                  column={column}
                  filterData={filterData}
                  filterKey={column.filterKey}
                  handleOpenFilter={column.handleOpenFilter}
                  title={column.title}
                />
              ))}
            </StyledTableRow>
          </TableHead>

          <TableBody>
            {!data || data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                  <Typography
                    fontFamily="Poppins"
                    fontSize={13}
                    color="text.secondary"
                  >
                    No sales entries found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((entry, index) => {
                return (
                  <StyledTableRow key={entry._id}>
                    {TableSchema.map((column) => (
                      <StyledTableCell
                        key={column.key}
                        sx={{ width: column.width }}
                        align={column.key === "action" ? "right" : "left"}
                      >
                        {column.render ? column.render(entry, index) : "—"}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableScrollLock={true}
      >
        <FilterModal
          open={openFilterModal}
          onClose={handleCloseFilter}
          options={selectedFilterData?.options || []}
          selectedValues={selectedFilterData?.filter || []}
          onSave={handleSaveFilter}
          currentSortOrder={selectedFilterData?.sort}
          filterKey={selectedFilterData?.key}
          hasMore={selectedFilterData?.loadMoreButton}
          displayKey={selectedFilterData?.displayKey}
          id={selectedFilterData?.id}
          fetchApi={selectedFilterData?.fetchApi}
          searchKey={selectedFilterData?.searchKey}
          type={selectedFilterData?.type}
        />
      </Popover>

      <DeleteConfirmationModal
        open={Boolean(deleteTarget)}
        handleClose={() => setDeleteTarget(null)}
        handleDelete={handleDelete}
        title={`Delete entry for "${
          deleteTarget?.dimension === "product_type"
            ? deleteTarget?.product_type
            : deleteTarget?.category?.name
        }" in ${deleteTarget?.month}/${deleteTarget?.year}?`}
      />

      {/* ── Revenue Mismatch Dialog ─────────────────────────────────────── */}
      <Dialog
        open={Boolean(mismatchEntry)}
        onClose={() => setMismatchEntry(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <WarningAmberIcon sx={{ color: "#f59e0b", fontSize: 28 }} />
              <Typography fontFamily="Poppins" fontSize={16} fontWeight={600}>
                Revenue Mismatch Detected
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setMismatchEntry(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Typography
            fontFamily="Poppins"
            fontSize={13}
            color="text.secondary"
            mb={2}
          >
            The <strong>Analytics Revenue</strong> (used for reporting and
            dashboards) does not match the <strong>Calculated Revenue</strong>{" "}
            derived from orders. Please review and decide how to proceed.
          </Typography>

          <Box
            display="flex"
            gap={2}
            p={1.5}
            sx={{ backgroundColor: "#f9fafb", borderRadius: 1 }}
          >
            <Box flex={1}>
              <Typography
                fontFamily="Poppins"
                fontSize={11}
                fontWeight={600}
                color="text.secondary"
                mb={0.5}
              >
                ANALYTICS REVENUE (total_revenue)
              </Typography>
              <Typography
                fontFamily="Poppins"
                fontSize={18}
                fontWeight={700}
                color="text.primary"
              >
                ${mismatchEntry?.total_revenue?.toFixed(2) ?? "—"}
              </Typography>
            </Box>
            <Box flex={1}>
              <Typography
                fontFamily="Poppins"
                fontSize={11}
                fontWeight={600}
                color="text.secondary"
                mb={0.5}
              >
                CALCULATED REVENUE (from orders)
              </Typography>
              <Typography
                fontFamily="Poppins"
                fontSize={18}
                fontWeight={700}
                color="#f59e0b"
              >
                ${mismatchEntry?.calculated_revenue?.toFixed(2) ?? "—"}
              </Typography>
            </Box>
          </Box>

          <Typography
            fontFamily="Poppins"
            fontSize={12}
            color="text.secondary"
            mt={2}
          >
            Choosing <strong>"Sync to Calculated"</strong> will update the
            analytics revenue to match the calculated value. This affects all
            reports using this category's revenue.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            sx={{ textTransform: "none", fontFamily: "Poppins" }}
            onClick={() => setMismatchEntry(null)}
          >
            I Understand, Keep As Is
          </Button>
          <Button
            variant="contained"
            size="small"
            color="warning"
            disabled={syncLoading}
            sx={{ textTransform: "none", fontFamily: "Poppins" }}
            onClick={handleSyncRevenue}
          >
            {syncLoading ? (
              <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
            ) : null}
            Sync to Calculated
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SalesByCategoryList;
